// Màn thanh toán: chọn gói → tạo đơn → quét QR → chờ webhook cấp quyền.
//
// BA MÀN TRONG MỘT, KHÔNG PHẢI BA MÀN RIÊNG. Tách ra thì người dùng bấm back
// giữa chừng là rơi vào màn trắng, và đóng tab là mất đơn đang chờ — mà đơn đó
// vẫn còn sống trên server, nên mở lại phải thấy tiếp chứ không phải thấy trắng.
//
// POLL, KHÔNG PHẢI WEBSOCKET
// Webhook SePay tới server của ta, không tới trình duyệt. Nên trình duyệt không
// có cách nào biết tiền đã về ngoài việc hỏi lại. Poll mỗi 3 giây trong lúc
// đang chờ là đủ: người dùng đang nhìn màn QR, vài giây chậm không ai nhận ra.
// Dừng ngay khi thấy đơn hết `pending` — poll tiếp là hỏi vô ích.
//
// ĐƯỜNG `play` KHÔNG CÓ Ở ĐÂY
// Trong app Google Play, UI mua là của Google; dẫn người dùng ra web để mua là
// vi phạm chương trình của Play. Nên màn này chỉ phục vụ web, và nếu server trả
// provider=`play` thì ta không dựng gì — xem `pay` bên dưới.

import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'
import type { Order, Plan } from '../types'
import { ApiError } from '../types'
import { Label, Note, Screen, Shell } from '../components/shell'
import { TopBar } from '../components/ui'

/** Bao lâu giữa hai lần hỏi trạng thái. Xem ghi chú ở đầu file. */
const POLL_MS = 3000

/** Số tiền theo kiểu Việt Nam: 200000 → "200.000". */
function vnd(n: number): string {
  return n.toLocaleString('vi-VN')
}

/** "1 tháng" / "3 tháng" / "12 tháng" — không dùng "tháng" cho số 1 là sai ngữ
 *  nghĩa tiếng Việt ("1 tháng" vẫn đúng, nhưng "3 tháng" thì số nhiều; giữ
 *  nguyên dạng số cho gọn và tránh rơi vào việc chia lẻ số 1). */
function monthsLabel(m: number): string {
  return `${m} tháng`
}

/** Đổi lỗi API thành câu người dùng hiểu được.
 *
 *  Riêng `sepay_not_configured` là đáng nói riêng. Server cố ý CHE detail của
 *  mọi lỗi 5xx (xem `writeErr`) — đúng về bảo mật, nên client nhận đúng câu
 *  "Lỗi hệ thống. Đã ghi log." và người dùng không biết làm gì. Nhưng `code`
 *  thì KHÔNG bị che, và `code` này nói thẳng là chưa cấu hình. Người chạy
 *  máy thì đọc được, và đó chính là người cần biết. */
function friendly(a: ApiError): string {
  switch (a.code) {
    case 'offline':
      return 'Không có mạng.'
    case 'sepay_not_configured':
      return 'Máy chủ chưa cấu hình tài khoản ngân hàng SePay. Người quản trị cần đặt SEPAY_ACCOUNT và SEPAY_BANK.'
    case 'no_such_plan':
      return 'Gói này không còn bán.'
    default:
      return a.detail
  }
}

export function PayView({
  hasFull,
  onBack,
  onDone,
}: {
  hasFull: boolean
  onBack: () => void
  onDone: () => void
}) {
  const [plans, setPlans] = useState<Plan[]>([])
  const [picked, setPicked] = useState<number | null>(null)
  const [order, setOrder] = useState<Order | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  // Đơn đang chờ, đã biết là có. Một lần poll trả về `null` nghĩa là hết hạn
  // hoặc đã xử lý, nên phải DỪNG poll — nếu không thì cứ hỏi mãi một đơn không
  // còn tồn tại.
  const waitingRef = useRef(false)

  useEffect(() => {
    let alive = true
    api
      .plans()
      .then((r) => {
        if (!alive) return
        setPlans(r.plans)
        setPicked(r.plans.length ? r.plans[0].months : null)
      })
      .catch((e) => {
        if (alive) setErr((e as ApiError).detail)
      })
    return () => {
      alive = false
    }
  }, [])

  const poll = useCallback(async () => {
    try {
      const r = await api.currentOrder()
      if (r.order === null) {
        // Hết đơn chờ: dừng poll. Có thể đơn vừa được cấp quyền (đã thành
        // `paid`, nên `pending` không còn nữa) — nên coi như xong.
        waitingRef.current = false
        setOrder(null)
        onDone()
        return
      }
      setOrder(r.order)
    } catch {
      // Mất mạng thì im lặng và thử lại ở vòng sau. Báo lỗi ở đây thì màn nhấp
      // nháy đỏ mỗi 3 giây, tệ hơn là im rồi tự hồi phục.
    }
  }, [onDone])

  useEffect(() => {
    if (!waitingRef.current) return
    const t = setInterval(poll, POLL_MS)
    return () => clearInterval(t)
  }, [poll, order])

  // Mở lại màn khi đã có đơn chờ từ trước (đóng tab rồi quay lại, hoặc F5).
  //
  // Không làm thế này thì đóng tab lúc đang chờ tiền xong mở lại thấy màn trắng,
  // tưởng mất tiền — trong khi đơn vẫn còn sống và webhook sẽ về bình thường.
  useEffect(() => {
    let alive = true
    api
      .currentOrder()
      .then((r) => {
        if (!alive || !r.order) return
        setOrder(r.order)
        waitingRef.current = true
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [])

  async function buy() {
    if (picked === null) return
    setBusy(true)
    setErr('')
    try {
      const o = await api.createOrder(picked)
      if (o.provider === 'play') {
        // Không xảy ra với web, nhưng nếu cấu hình sai thì phải nói thật thay
        // vì hiện QR rỗng — người dùng quét cái gì không hiểu.
        setErr('Gói này được mua trong ứng dụng Google Play, không phải trên web.')
        return
      }
      setOrder(o)
      waitingRef.current = true
    } catch (e) {
      setErr(friendly(e as ApiError))
    } finally {
      setBusy(false)
    }
  }

  // ---- đã có đơn: hiện QR và chờ ----
  if (order) {
    return (
      <Shell>
        <Screen>
          <TopBar title="Thanh toán" onBack={onBack} />
          <div className="card text-center">
            <Label className="mb-3 block">Số tiền</Label>
            <div className="font-serif text-3xl font-semibold text-ink">
              {vnd(order.amount_vnd)}
              <span className="ml-1 text-base font-normal text-ink-soft">đ</span>
            </div>
            <Note className="mt-1">
              {monthsLabel(order.plan_months)} — mỗi lần mua cộng thêm vào thời
              gian đang có, không thay thế.
            </Note>

            {/* Ảnh QR là ảnh TĨNH từ vietqr.app, không phải API của SePay: mã
                nằm sẵn trong nội dung chuyển khoản nên SePay tự khớp đơn. */}
            <img
              src={order.pay_url}
              alt="Mã QR chuyển khoản"
              className="mx-auto my-4 aspect-square w-full max-w-64 rounded-lg border border-ink/10 bg-white p-2"
            />

            <Note>
              Mở app ngân hàng → quét mã này → chuyển đúng số tiền. Nội dung
              chuyển khoản đã điền sẵn, không sửa.
            </Note>
          </div>

          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-ink-soft">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-accent" />
            Đang chờ máy chủ xác nhận…
          </div>
          <Note className="mt-1 text-center">
            Thường mất vài giây. Nếu lâu hơn một phút, kiểm tra lại số tiền và
            tài khoản nhận rồi thử quét lại.
          </Note>

          <div className="mt-4">
            <button onClick={onBack} className="btn btn-ghost border border-ink/15">
              Tạm để sau
            </button>
          </div>
        </Screen>
      </Shell>
    )
  }

  // ---- chọn gói ----
  return (
    <Shell>
      <Screen>
        <TopBar title="Gia hạn" onBack={onBack} />

        {hasFull && (
          <div className="card mb-3 border-good/30 bg-good-soft">
            <div className="text-sm text-good">
              Bạn đang có quyền toàn bộ kho câu hỏi. Gói ở đây chỉ cần khi muốn
              dùng lâu hơn.
            </div>
          </div>
        )}

        <Note className="mb-3">
          {/* ⚠️ GIÁ TẠM — chưa chốt với team, xem `plans` ở internal/api/orders.go. */}
          Giá dưới đây đang là giá tạm. Mua nhiều lần được, mỗi lần cộng thêm
          thời gian đang có. Không hoàn tiền.
        </Note>

        {err && (
          <div className="mb-3 rounded-lg border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad" role="alert">
            {err}
          </div>
        )}

        <div className="space-y-2">
          {plans.map((p) => {
            const on = picked === p.months
            return (
              <button
                key={p.months}
                onClick={() => setPicked(p.months)}
                className={
                  'w-full rounded-xl border px-3 py-3 text-left transition-colors ' +
                  (on ? 'border-accent bg-accent-soft' : 'border-ink/15 bg-surface')
                }
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-semibold text-ink">{monthsLabel(p.months)}</span>
                  <span className="font-semibold text-accent">{vnd(p.amount_vnd)} đ</span>
                </div>
                {p.months > 1 && (
                  <Note className="mt-0.5">
                    {vnd(Math.round(p.amount_vnd / p.months))} đ/tháng —{' '}
                    {p.months === 3 ? 'tiết kiệm 10%' : 'tiết kiệm 20%'}
                  </Note>
                )}
              </button>
            )
          })}
        </div>

        <div className="mt-4">
          <button
            onClick={buy}
            disabled={busy || picked === null}
            className={'btn btn-primary ' + (busy ? 'btn-off' : '')}
          >
            {busy ? 'Đang tạo đơn…' : 'Tạo đơn và quét mã'}
          </button>
        </div>

        <Note className="mt-2 text-center">
          Thanh toán bằng chuyển khoản ngân hàng qua SePay.
        </Note>
      </Screen>
    </Shell>
  )
}
